import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { splitSpecialties } from '../utils/specialty';
import { X, Check, Plus } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Match-key must mirror the backend `_specialty_match_key`: lowercase,
 * strip diacritics, collapse non-alnum to single spaces. Lets the UI snap
 * "MIRROR BASED THERAPY" to the already-existing "Mirror-Based Therapy".
 */
function matchKey(value) {
    const stripped = (value || '')
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
    const out = [];
    let prevSpace = true;
    for (const ch of stripped) {
        const isAlnum = /[a-z0-9]/.test(ch);
        if (isAlnum) {
            out.push(ch);
            prevSpace = false;
        } else if (!prevSpace) {
            out.push(' ');
            prevSpace = true;
        }
    }
    return out.join('').trim();
}

/**
 * Chip-style specialty picker with case/punctuation-insensitive autocomplete.
 * value: comma-separated string (same shape the API expects).
 */
export default function SpecialtyInput({ value = '', onChange, placeholder, testIdPrefix = 'specialty' }) {
    const [known, setKnown] = useState([]);
    const [input, setInput] = useState('');
    const [open, setOpen] = useState(false);
    const [highlight, setHighlight] = useState(0);
    const wrapperRef = useRef(null);
    const tags = splitSpecialties(value);
    const tagKeys = new Set(tags.map(matchKey));

    useEffect(() => {
        axios.get(`${API_URL}/api/specialties`).then(({ data }) => {
            setKnown(Array.isArray(data) ? data : []);
        }).catch(() => setKnown([]));
    }, []);

    useEffect(() => {
        if (!open) return;
        const onClick = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, [open]);

    const inputKey = matchKey(input);
    const suggestions = input.trim()
        ? known.filter(s => {
            const k = matchKey(s);
            if (!k) return false;
            if (tagKeys.has(k)) return false;
            return k.includes(inputKey);
        }).slice(0, 8)
        : known.filter(s => !tagKeys.has(matchKey(s))).slice(0, 8);

    const exactExists = inputKey && known.some(s => matchKey(s) === inputKey);
    const alreadyAdded = inputKey && tagKeys.has(inputKey);
    const canCreateNew = inputKey && !exactExists && !alreadyAdded;

    const commitTag = (tag) => {
        const clean = (tag || '').trim();
        if (!clean) return;
        const k = matchKey(clean);
        if (!k || tagKeys.has(k)) return;
        // Snap to existing canonical form if we have it.
        const canonical = known.find(s => matchKey(s) === k) || clean;
        const nextTags = [...tags, canonical];
        onChange(nextTags.join(', '));
        setInput('');
        setHighlight(0);
    };

    const removeTag = (tag) => {
        const nextTags = tags.filter(t => matchKey(t) !== matchKey(tag));
        onChange(nextTags.join(', '));
    };

    const handleKey = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (open && suggestions[highlight]) commitTag(suggestions[highlight]);
            else if (input.trim()) commitTag(input);
        } else if (e.key === 'Backspace' && !input && tags.length) {
            removeTag(tags[tags.length - 1]);
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlight(h => Math.min((suggestions.length - 1) + (canCreateNew ? 1 : 0), h + 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight(h => Math.max(0, h - 1));
        } else if (e.key === 'Escape') {
            setOpen(false);
        } else if (e.key === ',') {
            e.preventDefault();
            if (input.trim()) commitTag(input);
        }
    };

    return (
        <div ref={wrapperRef} className="relative">
            <div
                className="w-full border border-[#E5E0D8] rounded-sm px-2 py-2 flex flex-wrap items-center gap-1.5 bg-white focus-within:border-[#5B2D8E]"
                onClick={() => wrapperRef.current?.querySelector('input')?.focus()}
                data-testid={`${testIdPrefix}-chips`}
            >
                {tags.map(t => (
                    <span
                        key={matchKey(t)}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-[#F2EBE5] text-[#1A1A2E] rounded-sm"
                        data-testid={`${testIdPrefix}-chip-${matchKey(t)}`}
                    >
                        {t}
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); removeTag(t); }}
                            className="hover:text-[#5B2D8E]"
                            aria-label={`Remove ${t}`}
                        >
                            <X className="w-3 h-3" />
                        </button>
                    </span>
                ))}
                <input
                    type="text"
                    value={input}
                    onChange={(e) => { setInput(e.target.value); setOpen(true); setHighlight(0); }}
                    onFocus={() => setOpen(true)}
                    onKeyDown={handleKey}
                    placeholder={tags.length ? '' : (placeholder || 'Add a specialty — pick existing or type new')}
                    className="flex-1 min-w-[140px] border-0 outline-none focus:ring-0 text-sm py-1 px-1 bg-transparent"
                    data-testid={`${testIdPrefix}-input`}
                />
            </div>

            {open && (suggestions.length > 0 || canCreateNew) && (
                <div
                    className="absolute z-30 left-0 right-0 mt-1 bg-white border border-[#E5E0D8] rounded-sm shadow-lg max-h-64 overflow-y-auto"
                    data-testid={`${testIdPrefix}-menu`}
                >
                    {suggestions.map((s, idx) => (
                        <button
                            key={s}
                            type="button"
                            onClick={() => commitTag(s)}
                            onMouseEnter={() => setHighlight(idx)}
                            className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left ${highlight === idx ? 'bg-[#F9F6F0]' : 'hover:bg-[#F9F6F0]'}`}
                            data-testid={`${testIdPrefix}-suggestion-${matchKey(s)}`}
                        >
                            <Check className="w-3.5 h-3.5 text-[#5B2D8E]" />
                            {s}
                        </button>
                    ))}
                    {canCreateNew && (
                        <button
                            type="button"
                            onClick={() => commitTag(input)}
                            onMouseEnter={() => setHighlight(suggestions.length)}
                            className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left border-t border-[#E5E0D8] ${highlight === suggestions.length ? 'bg-[#F9F6F0]' : 'hover:bg-[#F9F6F0]'}`}
                            data-testid={`${testIdPrefix}-create-new`}
                        >
                            <Plus className="w-3.5 h-3.5 text-[#5B2D8E]" />
                            <span>Create new specialty "<strong>{input.trim()}</strong>"</span>
                        </button>
                    )}
                </div>
            )}

            <p className="text-xs text-[#4A4A68] mt-1">
                Enter or comma to add. Existing specialties are suggested — pick one to keep the list clean, or type a brand-new tag.
            </p>
        </div>
    );
}
